import {createSlice, createAsyncThunk} from "@reduxjs/toolkit";
import {RootState} from "../store";
import {IServiceData} from "./i-service";
import {getDefaultUserServiceService, updateUserServiceService} from "./service-service";

interface IInitialState {
  services: {
    id: number;
    userId: number;
    name: string;
    description: string;
    status_id: number;
    callDuration: number;
    callPriceCurrency: string;
    callPrice: number;
    hideServiceFromMenu: boolean;
    videoLocation: boolean;
    audioLocation: boolean;
    bookingQuestions: [
      {
        question: string;
        required: boolean;
      }
    ];
    createdAt: Date;
    updatedAt: Date;
    statusName: string;
  }[];
  serviceData: {
    id?: string;
    name?: string;
    slug?: string;
    description?: string;
    callDuration?: string | number;
    callPriceCurrency?: string;
    callPrice?: string | number;
    hideServiceFromMenu?: boolean;
    videoLocation?: boolean;
    audioLocation?: boolean;
    isPaused?: boolean;
    bookingQuestions?: any;
    statusName?: string;
  };
  isError: boolean;
  isSuccess: boolean;
  isSuccessCreate: boolean;
  isSuccessUpdate: boolean;
  isLoading: boolean;
  fetchStatus: "idle" | "pending" | "resolved" | "rejected";
  updateByIdStatus: "idle" | "pending" | "resolved" | "rejected";
  deleteStatus: "idle" | "pending" | "resolved" | "rejected";
  message: string;
  errorStatusCode: null;
}

const initialState: IInitialState = {
  services: [],
  serviceData: {},
  isError: false,
  isSuccess: false,
  isSuccessCreate: false,
  isSuccessUpdate: false,
  isLoading: false,
  fetchStatus: "idle",
  updateByIdStatus: "idle",
  deleteStatus: "idle",
  message: "",
  errorStatusCode: null,
};

//Get default user service
export const getDefaultUserService = createAsyncThunk<
  // Return type of the payload creator
  any,
  // First argument to the payload creator
  null,
  {
    // Optional fields for defining thunkApi field types
    state: RootState;
  }
>("service/getUserServices", async (_, thunkAPI) => {
  try {
    const token: string = thunkAPI.getState().userLogin.userInfo!.accessToken;
    return await getDefaultUserServiceService(token);
  } catch (error: any) {
    // console.log("profile-error", error);
    const errorStatusCode = error.response.status;
    const message =
      (error.response && error.response.data && error.response.data.message) ||
      error.message ||
      error.toString();
    return thunkAPI.rejectWithValue({message, errorStatusCode});
  }
});

//Update user service
export const updateUserService = createAsyncThunk<
  // Return type of the payload creator
  any,
  // First argument to the payload creator
  IServiceData,
  {
    // Optional fields for defining thunkApi field types
    state: RootState;
  }
>("service/updateUserService[id]", async (serviceData, thunkAPI) => {
  try {
    const token: string = thunkAPI.getState().userLogin.userInfo!.accessToken;
    return await updateUserServiceService(serviceData, token);
  } catch (error: any) {
    const message =
      (error.response && error.response.data && error.response.data.message) ||
      error.message ||
      error.toString();
    return thunkAPI.rejectWithValue(message);
  }
});

export const serviceSlice = createSlice({
  name: "service",
  initialState,
  reducers: {
    resetUserServiceState: (state) => {
      state.isError = false;
      state.isLoading = false;
      state.isSuccess = false;
      state.isSuccessCreate = false;
      state.isSuccessUpdate = false;
      state.message = "";
      state.errorStatusCode = null;
      state.fetchStatus = "idle";
      state.updateByIdStatus = "idle";
      state.serviceData = {};
      state.services = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getDefaultUserService.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getDefaultUserService.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.errorStatusCode = null;
        state.serviceData = action.payload.data;
      })
      .addCase(getDefaultUserService.rejected, (state, action: any) => {
        // console.log("payload", action.payload);
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload.message;
        state.errorStatusCode = action.payload.errorStatusCode;
        state.serviceData = {};
      })
      .addCase(updateUserService.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateUserService.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccessUpdate = true;
        state.services = action.payload.data;
      })
      .addCase(updateUserService.rejected, (state, action: any) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
        state.errorStatusCode = null;
        state.services = [];
      });
  },
});

export const {resetUserServiceState} = serviceSlice.actions;

export default serviceSlice.reducer;
